import React, { type ReactNode } from 'react';
import { Label } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import Toggle from '@atlaskit/toggle';
import { createIndexedDBStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/indexed-db/index.tsx';
import type { AsyncStorage } from '@atlassian/jira-browser-storage-providers/src/types.tsx';
import { InProductHelpButton } from '@atlassian/jira-insights-common/src/common/ui/in-product-help-button/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	FeatureContainer,
	FeatureSubtitle,
	FeatureTextContainer,
	FeatureToggle,
	FeatureToggleText,
} from '../../../common/ui/styled';
import messages from './messages';

// Article ID for related help article
const ARTICLE_ID = '6q0itr2i2wrpSFNhHIx42g';
const ANALYTICS_ID = `products-jsw.features.local-device-cache.help-article-${ARTICLE_ID}`;

export type FeatureSectionProps = {
	featureKey: string;
	value: boolean;
	onUpdateFeature: (featureKey: string, value: boolean) => void;
};

export const LocalDeviceCacheSection = (props: FeatureSectionProps) => {
	const { featureKey, value, onUpdateFeature } = props;
	const { formatMessage } = useIntl();
	const cmpBoardCache: AsyncStorage = createIndexedDBStorageProvider('cmp-board');
	const cmpBacklogCache: AsyncStorage = createIndexedDBStorageProvider('cmp-backlog');

	const handleOnChange = () => {
		// Clear everything if the toggle was on
		// Site users will have data cleared next board or backlog visit
		if (value) {
			cmpBoardCache.clearAll();
			cmpBacklogCache.clearAll();
		}
		onUpdateFeature(featureKey, !value);
	};

	return (
		<FeatureContainer
			key={featureKey}
			data-testid="products-jsw-configuration-main.ui.features-section.local-device-cache"
		>
			<FeatureTextContainer>
				<Heading level="h500" as="h2">
					{formatMessage(messages.title)}
				</Heading>
			</FeatureTextContainer>
			<FeatureToggleText>
				<FeatureSubtitle>
					<Label htmlFor="toggle-feature-local-device-cache">
						{formatMessage(messages.description, {
							button: (chunks: ReactNode) => (
								<InProductHelpButton
									articleId={ARTICLE_ID}
									articleAnalyticsId={ANALYTICS_ID}
									appearance="link"
								>
									{chunks}
								</InProductHelpButton>
							),
						})}
					</Label>
				</FeatureSubtitle>
			</FeatureToggleText>
			<FeatureToggle>
				<Toggle
					id="toggle-feature-local-device-cache"
					name={featureKey}
					label={
						value
							? formatMessage(messages.toggleLabelOptingIn)
							: formatMessage(messages.toggleLabelOptingOut)
					}
					isChecked={value}
					onChange={handleOnChange}
				/>
			</FeatureToggle>
		</FeatureContainer>
	);
};
